/**
 *  @class
 *  @function CartDiscounts
 */
if (!customElements.get("cart-discounts")) {
  class CartDiscounts extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      // No-op: listeners are attached document-wide below so they
      // survive every innerHTML re-render of the cart drawer.
    }

    getSectionsToRender() {
      return [
        {
          id: "Cart",
          section: "main-cart",
          selector: ".thb-cart-form",
        },
        {
          id: "Cart-Drawer",
          section: "cart-drawer",
          selector: ".cart-drawer",
        },
        {
          id: "cart-drawer-toggle",
          section: "cart-bubble",
          selector: ".thb-item-count",
        },
      ];
    }
    getSectionInnerHTML(html, selector) {
      return new DOMParser()
        .parseFromString(html, "text/html")
        .querySelector(selector).innerHTML;
    }
    updateDiscount() {
      const discountCode = this.querySelector(".cart-discounts--input");
      const discountCodeValue = discountCode.value;
      const existingDiscounts = this.existingDiscounts();

      if (existingDiscounts.includes(discountCodeValue)) return;
      existingDiscounts.push(discountCodeValue);

      this.renderDiscounts(existingDiscounts, discountCodeValue);
    }
    removeDiscount(removeButton) {
      const pill = removeButton.previousElementSibling;
      const discountCode = pill.innerHTML;
      const existingDiscounts = this.existingDiscounts();
      const index = existingDiscounts.indexOf(discountCode);
      if (index === -1) return;

      existingDiscounts.splice(index, 1);

      this.renderDiscounts(existingDiscounts);
    }
    existingDiscounts() {
      const discountCodes = [];
      const discountPills = this.querySelectorAll(".cart-discounts--name");
      for (const pill of discountPills) {
        discountCodes.push(pill.innerHTML);
      }

      return discountCodes;
    }
    renderDiscounts(existingDiscounts, discountCodeValue) {
      const body = JSON.stringify({
        discount: existingDiscounts.join(","),
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      });
      this.querySelector(".cart-discounts--error").setAttribute("hidden", "");
      fetch(`${theme.routes.cart_update_url}.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: `application/json`,
        },
        ...{
          body,
        },
      })
        .then((response) => {
          return response.text();
        })
        .then((state) => {
          const parsedState = JSON.parse(state);

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document
                .getElementById(section.id)
                ?.querySelector(section.selector) ||
              document.getElementById(section.id);

            if (parsedState.sections && elementToReplace) {
              elementToReplace.innerHTML = this.getSectionInnerHTML(
                parsedState.sections[section.section],
                section.selector,
              );
            }
          });

          // After re-rendering, `this` is detached from the DOM.
          // Query the freshly rendered cart-discounts element instead.
          if (!discountCodeValue) return;

          setTimeout(() => {
            const freshEl = document.querySelector(
              "#Cart-Drawer cart-discounts, #Cart cart-discounts",
            );
            const errorEl = freshEl
              ? freshEl.querySelector(".cart-discounts--error")
              : null;
            const successEl = freshEl
              ? freshEl.querySelector(".cart-discounts--success")
              : null;

            // Case-insensitive check: Shopify uppercases codes in discount_codes
            const normalizedInput = discountCodeValue.toUpperCase().trim();

            // For success, trust the rendered DOM — if a pill appeared with
            // this code, Shopify accepted it (works regardless of plan tier
            // or line-level vs cart-level discount application).
            const appliedPills = freshEl
              ? freshEl.querySelectorAll(".cart-discounts--name")
              : [];
            const isApplied = Array.from(appliedPills).some(
              (pill) =>
                pill.textContent.trim().toUpperCase() === normalizedInput,
            );

            // Anything that isn't reflected as an applied pill counts as
            // invalid — don't rely on Shopify explicitly flagging the code
            // as `applicable: false` in discount_codes, since a code that
            // doesn't exist at all can be silently omitted from that array
            // instead, which previously left the user with no feedback at all.
            if (!isApplied && errorEl) {
              errorEl.removeAttribute("hidden");
            } else if (isApplied && successEl) {
              // Grab the saved amount from the rendered discount row
              const appliedRow = Array.from(
                freshEl.querySelectorAll(".cart-discounts--row"),
              ).find((row) => {
                const name = row.querySelector(".cart-discounts--name");
                return (
                  name &&
                  name.textContent.trim().toUpperCase() === normalizedInput
                );
              });
              const amountEl = successEl.querySelector(
                ".cart-discounts--success-amount",
              );
              if (amountEl && appliedRow) {
                const rowAmount = appliedRow.querySelector(".amount");
                amountEl.textContent = rowAmount
                  ? rowAmount.textContent.trim()
                  : "";
              }
              successEl.removeAttribute("hidden");
              // Auto-hide the success message after 4 seconds
              setTimeout(() => successEl.setAttribute("hidden", ""), 4000);
            }
          }, 300);
        })
        .catch((error) => {
          console.error("Cart discount update failed:", error);
          // Sections weren't re-rendered, so the original error element is
          // still in the live DOM — safe to use `this` here.
          const errorEl = this.querySelector(".cart-discounts--error");
          if (errorEl) errorEl.removeAttribute("hidden");
        })
        .finally(() => {
          // Query fresh from the document: on success `this` is detached
          // (replaced by re-rendered sections) and already has a non-disabled
          // button; on failure the original button is still live and must
          // be unstuck here, or it stays disabled forever.
          document
            .querySelectorAll(
              "#Cart-Drawer .cart-discounts--button, #Cart .cart-discounts--button",
            )
            .forEach((btn) => {
              btn.disabled = false;
              btn.classList.remove("loading");
            });
        });
    }
  }
  customElements.define("cart-discounts", CartDiscounts);

  // Document-level delegation: set up once, survives all innerHTML re-renders.
  document.addEventListener("click", (event) => {
    const button = event.target.closest(".cart-discounts--button");
    const removeButton = event.target.closest(".cart-discounts--remove");

    if (button) {
      event.preventDefault();
      if (button.disabled) return;
      const cartDiscounts = button.closest("cart-discounts");
      if (!cartDiscounts) return;
      button.disabled = true;
      button.classList.add("loading");
      cartDiscounts.updateDiscount();
      return;
    }

    if (removeButton) {
      const cartDiscounts = removeButton.closest("cart-discounts");
      if (!cartDiscounts) return;
      cartDiscounts.removeDiscount(removeButton);
    }
  });
}
